import {
  PositionFill24Icon,
  PositionOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { isFuture } from "date-fns";
import ElementWrapper from "features/VehicleSidebar/VehicleList/common/ElementWrapper";
import { Header } from "features/VehicleSidebar/VehicleList/common/Header";
import { removeStationFromStopName } from "features/VehicleSidebar/VehicleList/utils/stopNameUtils";
import { generatePath, useSearchParams } from "react-router-dom";
import { CallButton } from "shared/components/buttons/CallButton";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import { Badge, Box, DropsTooltip, Flex, Text } from "shared/components/Spor";
import { AlternativeTransport } from "shared/types/alternativeTransport";
import { toSentenceCase } from "shared/utils/alternativeTransport";
import { BUS } from "shared/utils/routes";
import { useTheme } from "styled-components";

type Props = {
  alternativeTransport: AlternativeTransport;
  elementSelected: boolean;
  hasTracking: boolean;
};

export const AlternativeTransportElement = ({
  alternativeTransport,
  elementSelected,
  hasTracking,
}: Props) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const {
    scheduledArrivalTimeDestination,
    scheduledDepartureTimeOrigin,
    workShiftId,
    driverPhoneNumber,
    trainIds,
    originStopName,
    destinationStopName,
    isShuttleVehicle,
    vehicleStatus,
    externalSystem,
  } = alternativeTransport;

  const isCancelled = vehicleStatus === "CANCELLED";

  const headerText = [workShiftId, trainIds.join(", ")]
    .filter((str): str is string => str !== null)
    .join(" - ");

  const textDecorationStyle = {
    textDecoration: isCancelled ? "line-through" : "none",
  };

  /*
    This is a workaround. If we send in the plus to Puzzel, it is stripped away
    before the 18740 prefix is added. This works fine if you're an admin, but
    not for normal DROPS users. But if we replace the + with 00, it is not stripped,
    and with the 00 it works just fine. Don't ask me why.
   
    These formats work for everyone:
    18740 0047 12345678 <- Result from what we send in with the workaround
    18740 12345678
    0047 12345678
    12345678

    This format only works for admins:
    18740 47 12345678 <- Result from what we send in without the workaround
  */
  const phoneNumberWithDigitsOnly = driverPhoneNumber
    ? driverPhoneNumber.replace("+", "00")
    : null;

  function getVehicleLabel() {
    switch (vehicleStatus) {
      case "PLANNED":
        return <Badge semantic="warning">Ikke bekreftet</Badge>;
      case "CANCELLED":
        return <Badge semantic="alarm">Kansellert</Badge>;
      case "CONFIRMED":
        return driverPhoneNumber ? (
          <CallButton phoneNumber={phoneNumberWithDigitsOnly!} size="xs" />
        ) : null;
    }
  }

  const isFutureArrival =
    scheduledArrivalTimeDestination &&
    isFuture(scheduledArrivalTimeDestination);

  const textColorMain = isFutureArrival
    ? theme.colorTextMain
    : theme.colorTextSecondary;

  const textColorSecondary = isFutureArrival
    ? theme.colorTextSecondary
    : theme.colorTextDisabled;

  const backgroundColor = isFutureArrival
    ? theme.colorBackgroundSecondary
    : theme.colorBackgroundMain;

  const border = isFutureArrival
    ? undefined
    : `1px solid ${theme.colorOutlineSecondary}`;

  return (
    <ElementWrapper
      selected={elementSelected}
      backgroundColor={backgroundColor}
      color={textColorMain}
      border={border}
      isHover
      to={`${generatePath(BUS, { vehicleUuid: alternativeTransport.id })}?${searchParams.toString()}`}
    >
      <Box
        display="flex"
        flexDir="column"
        gap="3px"
        flex={1}
        _focusVisible={{
          borderRadius: "6px",
        }}
      >
        <Header>
          <Text
            fontWeight="bold"
            style={{
              display: "block",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              maxWidth: "9rem",
              ...textDecorationStyle,
              color: textColorMain,
            }}
            title={headerText}
            variant="sm"
          >
            {headerText}
          </Text>
          {isShuttleVehicle && (
            <Text color={textColorSecondary} style={{ ...textDecorationStyle }}>
              Pendel
            </Text>
          )}
          {externalSystem && (
            <Text variant="sm" color={theme.colorTextSecondary}>
              {toSentenceCase(externalSystem)}
            </Text>
          )}
        </Header>
        <Text
          style={{
            gridColumn: "2",
            gap: "3px",
            ...textDecorationStyle,
            color: textColorSecondary,
          }}
          variant="sm"
          title={`${removeStationFromStopName(originStopName) ?? ""}–${removeStationFromStopName(destinationStopName) ?? ""}`}
        >
          {`${removeStationFromStopName(originStopName) ?? ""}–${removeStationFromStopName(destinationStopName) ?? ""}`}
        </Text>
        <Text
          // NOTE TimeInterval returns a paragraph, and nesting them is not valid html
          as="div"
          style={{
            gridColumn: "2",
            ...textDecorationStyle,
            color: textColorMain,
          }}
          variant="sm"
        >
          <TimeInterval
            startTime={scheduledDepartureTimeOrigin}
            endTime={scheduledArrivalTimeDestination}
          />
        </Text>
      </Box>

      <Flex
        flexDir="column"
        gap="18px"
        alignItems="flex-end"
        justifyContent="space-between"
        alignSelf="stretch"
        zIndex={1}
      >
        {hasTracking ? (
          <DropsTooltip label="Denne bussen har aktiv sporing">
            <PositionFill24Icon color={theme.colorSuccess} />
          </DropsTooltip>
        ) : (
          <DropsTooltip label="Denne bussen har ikke aktiv sporing">
            <PositionOutline24Icon />
          </DropsTooltip>
        )}

        {getVehicleLabel()}
      </Flex>
    </ElementWrapper>
  );
};
